import { createContext, useContext, useEffect, type ReactNode } from "react";

type Theme = "dark";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({ theme: "dark", toggleTheme: () => {} });

/**
 * Light mode is intentionally killed for now — the Nike-style palette
 * is dark-first and light mode hadn't been tuned for it. Flip this back
 * to a stateful toggle when we want to support light mode again.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("dark");
    document.body.classList.add("dark-bg");
    try { localStorage.setItem("theme", "dark"); } catch {
      // localStorage can throw in private mode / blocked storage — theme still applies via class.
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme: "dark", toggleTheme: () => {} }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
